import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Quote, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, TextArea, ToggleRow, EmptyState, Skeleton } from "@/components/admin/Field";
import ImageUploadField from "@/components/admin/ImageUploadField";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

const EMPTY = { client_name: "", company: "", quote: "", avatar: "", rating: 5, published: true, sort_order: 0 };

export default function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data || []);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save() {
    if (!draft.client_name.trim() || !draft.quote.trim()) {
      toast.error("Name and quote are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: draft.client_name,
        company: draft.company,
        quote: draft.quote,
        avatar: draft.avatar,
        rating: Number(draft.rating) || 5,
        published: !!draft.published,
        sort_order: Number(draft.sort_order) || 0,
      };
      if (draft._isNew) {
        const { data } = await api.post("/admin/testimonials", payload);
        setItems((arr) => [...arr, data].sort((a, b) => a.sort_order - b.sort_order));
      } else {
        const { data } = await api.put(`/admin/testimonials/${draft.id}`, payload);
        setItems((arr) => arr.map((t) => (t.id === data.id ? data : t)).sort((a, b) => a.sort_order - b.sort_order));
      }
      toast.success("Saved");
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/testimonials/${pendingDelete.id}`);
      setItems((arr) => arr.filter((t) => t.id !== pendingDelete.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Testimonials</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Client quotes</h1>
          <p className="mt-1 font-body text-sm text-white/50">Only published quotes are shown on the public site.</p>
        </div>
        <button
          onClick={() => setDraft({ ...EMPTY, _isNew: true })}
          data-testid="testimonials-new-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff]"
        >
          <Plus className="h-4 w-4" /> New testimonial
        </button>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : items.length === 0 && !draft ? (
        <EmptyState
          icon={Quote}
          title="No testimonials yet"
          description="Add quotes from happy clients to build trust on the homepage."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((t) => (
            <article
              key={t.id}
              className="flex flex-col rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 transition hover:border-white/15"
            >
              <Quote className="h-5 w-5 text-[#0055ff]" />
              <p className="mt-3 font-display text-base font-medium leading-relaxed text-white/85">“{t.quote}”</p>
              <div className="mt-4 flex items-center gap-3">
                {t.avatar ? (
                  <img src={t.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.05] font-display text-sm text-white/80">
                    {t.client_name?.[0] || "?"}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate font-display text-sm text-white">{t.client_name}</div>
                  <div className="truncate font-body text-xs text-white/45">{t.company}</div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3 w-3 ${i < (t.rating || 0) ? "text-[#ffcf6c] fill-[#ffcf6c]" : "text-white/15"}`}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/[0.05] pt-3">
                <span
                  className={`font-body text-[10px] uppercase tracking-[0.25em] ${
                    t.published ? "text-emerald-300" : "text-white/35"
                  }`}
                >
                  {t.published ? "Published" : "Draft"}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDraft({ ...t, _isNew: false })}
                    className="rounded-full border border-white/12 px-3 py-1 font-body text-xs text-white/85 hover:border-white/25"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setPendingDelete(t)}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-white/12 text-white/55 hover:border-red-400/40 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-[110] flex items-end justify-end bg-black/60 backdrop-blur-md sm:items-center sm:justify-center">
          <div className="w-full max-w-xl rounded-t-3xl border border-white/10 bg-[#0a0a0c] p-6 sm:rounded-3xl sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-xl font-medium tracking-tight text-white">
                {draft._isNew ? "New testimonial" : "Edit testimonial"}
              </h3>
              <button onClick={() => setDraft(null)} className="text-white/55 hover:text-white">×</button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Client name" value={draft.client_name} onChange={(v) => setDraft({ ...draft, client_name: v })} testid="testimonial-name" />
                <Field label="Company" value={draft.company} onChange={(v) => setDraft({ ...draft, company: v })} />
              </div>
              <TextArea label="Quote" value={draft.quote} onChange={(v) => setDraft({ ...draft, quote: v })} rows={4} testid="testimonial-quote" />
              <ImageUploadField label="Avatar (optional)" value={draft.avatar} onChange={(v) => setDraft({ ...draft, avatar: v })} />
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Rating (0-5)" value={draft.rating} onChange={(v) => setDraft({ ...draft, rating: v })} />
                <Field label="Sort order" value={draft.sort_order} onChange={(v) => setDraft({ ...draft, sort_order: v })} />
              </div>
              <ToggleRow
                label="Published"
                description="Show this quote on the public site."
                value={!!draft.published}
                onChange={(v) => setDraft({ ...draft, published: v })}
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setDraft(null)} className="rounded-full border border-white/12 px-5 py-2.5 font-body text-sm text-white/75 hover:border-white/30 hover:text-white">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white hover:bg-[#1a66ff] disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this testimonial?"
        description={`“${pendingDelete?.client_name || ""}” will be removed.`}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
